import React from "react";
import { useCurrentFrame, interpolate, Easing } from "remotion";
import { z } from "zod";

// Zod schema for PopUpText props
const PopUpTextPropsSchema = z.object({
  title: z.string(),
  subtitle: z.string(),
  startFrame: z.number(),
  inDuration: z.number(),
  visibleForFrames: z.number(),
  outDuration: z.number(),
  x: z.number(),
  y: z.number(),
  titleFontSize: z.number(),
  subtitleFontSize: z.number(),
  titleColor: z.string(),
  subtitleColor: z.string(),
  backgroundColor: z.string(),
  padding: z.string(),
  borderRadius: z.string(),
  fontFamily: z.string(),
  backdropBlur: z.boolean(),
  maxWidth: z.number()
});

export { PopUpTextPropsSchema };

/**
 * PopUpText Component
 * - Creates a text overlay with header and subtitle
 * - Mimics CapCut's "Pop Up" animation effect
 * - Positioned by default at bottom left
 * - Includes backdrop blur and modern styling
 * - Animates in with scale and bounce, fades out smoothly
 */
export const PopUpText = ({
  title = "T7S Robot Vacuum Cleaner",
  subtitle = "2-in-1 Mopping and Vacuum",
  startFrame = 0,
  inDuration = 20,
  visibleForFrames = 90,
  outDuration = 15,
  x = 80,
  y = 80,
  titleFontSize = 58,
  subtitleFontSize = 36,
  titleColor = "#ffffff",
  subtitleColor = "#b0b0b0",
  backgroundColor = "rgba(0, 0, 0, 0.6)",
  padding = "24px 32px",
  borderRadius = "16px",
  fontFamily = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  backdropBlur = true,
  maxWidth = 600,
}) => {
  const frame = useCurrentFrame();
  const endFrame = startFrame + inDuration + visibleForFrames + outDuration;

  // Don't render if outside the active frame range
  if (frame < startFrame || frame > endFrame) {
    return null;
  }

  const relativeFrame = frame - startFrame;

  // Animation phases
  let opacity = 0;
  let scale = 0.3;
  let translateY = 40;

  if (relativeFrame <= inDuration) {
    // Pop-in animation with CapCut-style bounce
    const progress = relativeFrame / inDuration;
    
    opacity = interpolate(progress, [0, 0.8, 1], [0, 1, 1], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    });
    
    // Scale animation with bounce effect (overshoots then settles)
    scale = interpolate(progress, [0, 0.6, 0.8, 1], [0.3, 1.15, 0.95, 1], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
      easing: Easing.bezier(0.68, -0.55, 0.265, 1.55), // Bouncy easing
    });
    
    // Slide up from below
    translateY = interpolate(progress, [0, 1], [40, 0], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
      easing: Easing.out(Easing.cubic),
    });
  } else if (relativeFrame <= inDuration + visibleForFrames) {
    // Fully visible
    opacity = 1;
    scale = 1;
    translateY = 0;
  } else {
    // Fade out
    const fadeProgress = (relativeFrame - inDuration - visibleForFrames) / outDuration;
    opacity = interpolate(fadeProgress, [0, 1], [1, 0], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
      easing: Easing.out(Easing.cubic),
    });
    scale = 1;
    translateY = 0;
  }

  return (
    <div
      style={{
        position: "absolute",
        left: x,
        bottom: y,
        maxWidth: maxWidth,
        opacity,
        transform: `scale(${scale}) translateY(${translateY}px)`,
        transformOrigin: "bottom left",
        backgroundColor,
        padding,
        borderRadius,
        fontFamily,
        zIndex: 10,
        backdropFilter: backdropBlur ? "blur(10px)" : "none",
        WebkitBackdropFilter: backdropBlur ? "blur(10px)" : "none",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
      }}
    >
      {/* Title */}
      <div
        style={{
          fontSize: titleFontSize,
          fontWeight: "700",
          color: titleColor,
          lineHeight: "1.2",
          marginBottom: "8px",
          textShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
          letterSpacing: "-0.02em",
        }}
      >
        {title}
      </div>
      
      {/* Subtitle */}
      <div
        style={{
          fontSize: subtitleFontSize,
          fontWeight: "400",
          color: subtitleColor,
          lineHeight: "1.3",
          textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
          letterSpacing: "-0.01em",
        }}
      >
        {subtitle}
      </div>
    </div>
  );
};

export default PopUpText;

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
